import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { IconComponent } from '../../atoms/icon/icon.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { TooltipComponent } from '../../molecules/tooltip/tooltip.component';
import { TeaserComponent } from '../../molecules/teaser/teaser.component';
import { ModalComponent } from '../../molecules/modal/modal.component';
import { QuoteComponent } from '../../molecules/quote/quote.component';
import { SwiperSlide } from '../../../shared/interfaces/swiper-slide.interface';
import { Tooltip } from '../../../shared/interfaces/tooltip.interface';
import { SliderType } from '../../../shared/enums/slider-type.enums';
import TeaserType from '../../../shared/enums/teaser.type.enums';
import { ModalType } from '../../../shared/enums/modal-type.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { Video } from '../../../shared/interfaces/video.interface';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { CaasComponents } from '../../../shared/interfaces/caas/caas.interface';
import { ImageComponent } from '../../atoms/image/image.component';
import { SwiperComponent, SwiperModule } from 'swiper/angular';
import SwiperCore, { Navigation, Scrollbar, A11y } from 'swiper';
import { CapitalizePipe } from '../../capitalize.pipe';
SwiperCore.use([Navigation, Scrollbar, A11y]);
@Component({
  selector: 'hdi-slider',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    TooltipComponent,
    TeaserComponent,
    ModalComponent,
    ButtonComponent,
    QuoteComponent,
    ImageComponent,
    SwiperModule,
    SwiperModule,
    CapitalizePipe,
  ],
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  host: {
    id: crypto.randomUUID().toString(), // Ensure a unique host ID for each slider
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SliderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() caasData?: CaasComponents['value'][number];
  @Input() slides: SwiperSlide[] = [];
  @Input() bgColor!: Colors;
  @Input() sliderType: SliderType = SliderType.AWARD;
  @Input() slideStep = 1;
  @Input() sliderHeadline?: string;

  private sliderStates: { [key: string]: number } = {};

  showModal = false;
  currentVideo?: Video;
  routesSub$: Subscription = new Subscription();

  Sizes = Sizes;
  tooltip: Tooltip;
  TeaserType = TeaserType;
  ModalType = ModalType;
  ButtonType = ButtonType;
  Colors = Colors;
  SliderType = SliderType;
  host: HTMLElement;

  constructor(
    private routes: ActivatedRoute,
    private elementRef: ElementRef
  ) {
    this.tooltip = { id: '', description: '' };
    this.host = this.elementRef.nativeElement;
  }
  ngOnInit(): void {
    this.routesSub$ = this.routes.queryParams.subscribe((params: Params) => {
      if (params['videoId']) {
        this.currentVideo = this.slides.find(
          (slide) => slide.video?.videoId === params['videoId']
        )?.video;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      const sliderId = this.caasData?.identifier || this.host.id;
      this.sliderStates[sliderId] = 0; // Reset state for new slider ID
      this.setCaasContent();
    }
  }

  openTooltip(tooltip: Tooltip) {
    this.tooltip = tooltip;
  }

  closeTooltip() {
    this.tooltip = { id: '', description: '' };
  }

  updateCurrentSlide(direction: string) {
    const lastSlide = this.slides.length - 1;
    const sliderId = this.caasData?.identifier || this.host.id;
    const currentSlide = this.sliderStates[sliderId] ?? 0;

    let newSlide;
    if (direction === 'next') {
      newSlide = currentSlide === lastSlide ? 0 : currentSlide + 1;
    } else {
      newSlide = currentSlide === 0 ? lastSlide : currentSlide - 1;
    }

    this.sliderStates[sliderId] = newSlide;
  }
  scrollToNext(event: Event) {
    event.preventDefault();
    this.updateCurrentSlide('next');
    this.scrollToSlide();
  }

  scrollToPrev(event: Event) {
    event.preventDefault();
    this.updateCurrentSlide('prev');
    this.scrollToSlide();
  }

  private scrollToSlide() {
    const sliderId = this.caasData?.identifier || this.host.id;
    const sanitizedSliderId = CSS.escape(sliderId);
    const slideIndex = this.sliderStates[sliderId];
    const slide = this.host.querySelector(
      `#${sanitizedSliderId}-quote-${slideIndex}`
    );

    if (slide) {
      slide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    } else {
      console.error(
        `Slide not found: #${sanitizedSliderId}-quote-${slideIndex}`
      );
    }
  }
  openVideo(video?: Video) {
    this.currentVideo = video;
    this.showModal = true;
  }

  setCaasContent() {
    if (!this.caasData) return;

    switch (this.caasData?.template.uid) {
      case 'st_awards':
        this.sliderHeadline = this.caasData?.formData.st_header?.value;
        this.sliderType = SliderType.AWARD;
        this.slides = this.getCaasAwardSlides();
        break;
      case 'st_teasers':
        this.sliderType = SliderType.TEASER;
        this.slides = this.getCaasTeaserSlides();
        break;
      case 'st_quotes':
        this.sliderType = SliderType.QUOTES;
        this.slides = this.getCaasQuotesSlides();
        break;
      default:
        console.warn(
          'SliderComponent > setCaasContent > no valid uid',
          this.caasData?.template.uid
        );
        break;
    }
  }

  getCaasAwardSlides(): SwiperSlide[] {
    return (
      this.caasData?.formData.st_items?.value.map((awardItem) => ({
        imageSrc: awardItem.formData.st_image?.value.url || '',
        imageAlt: awardItem.formData.st_image_title?.value || '',
        tooltip: {
          id: awardItem.identifier,
          description: `<span>${awardItem.formData.st_tooltip_text?.value}</span>`,
        },
      })) || []
    );
  }

  getCaasQuotesSlides(): SwiperSlide[] {
    // console.log('getCaasQuotesSlides > caasData', this.caasData);
    return (
      this.caasData?.formData?.st_items?.value.map((quoteItem) => ({
        quote: quoteItem.formData.st_text?.value || '',
        author: quoteItem.formData.st_person?.value || '',
        subline: quoteItem.formData.st_subline?.value || '',
      })) || []
    );
  }

  getCaasTeaserSlides(): SwiperSlide[] {
    return [];
  }

  ngOnDestroy(): void {
    this.routesSub$.unsubscribe();
  }
  @ViewChild(SwiperComponent) swiper!: SwiperComponent;
  swipePrev() {
    this.swiper.swiperRef.slidePrev();
  }
  swipeNext() {
    this.swiper.swiperRef.slideNext();
  }
}
